<template>
  <template v-if="!isResult">
    <p>{{ dateFormatMMDD(detailData.dakoku_date) }}</p>

    <div class="form-item">
      <label>名前</label>
      <div class="before-after">
        <p>{{ detailData.staff_name }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>就業場所</label>
      <div class="before-after">
        <p>{{ detailData.syuugyou_basyo_name }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>出勤</label>
      <div class="before-after">
        <p class="before">{{ detailData.syukkin_round_datetime ? timeFormatter(detailData.syukkin_round_datetime) : '記録なし' }}</p>
        <p class="after">{{ detailData.syukkin_datetime ? '(' + timeFormatter(detailData.syukkin_datetime) + ')' : '' }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>退勤</label>
      <div class="before-after">
        <p class="before">{{ detailData.taikin_round_datetime ? timeFormatter(detailData.taikin_round_datetime) : '記録なし' }}</p>
        <p class="after">{{ detailData.taikin_datetime ? '(' + timeFormatter(detailData.taikin_datetime) + ')' : '' }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>外出</label>
      <div class="before-after">
        <p class="before">{{ detailData.gaisyutsu_round_datetime ? timeFormatter(detailData.gaisyutsu_round_datetime) : '記録なし' }}</p>
        <p class="after">{{ detailData.gaisyutsu_datetime ? '(' +  timeFormatter(detailData.gaisyutsu_datetime) + ')' : '' }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>戻り</label>
      <div class="before-after">
        <p class="before">{{ detailData.modori_round_datetime ? timeFormatter(detailData.modori_round_datetime) : '記録なし' }}</p>
        <p class="after">{{ detailData.modori_datetime ? '(' +  timeFormatter(detailData.modori_datetime) + ')' : '' }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>休憩</label>
      <div class="before-after">
        <p class="before">{{ detailData.kyuukei_round_times ? timeToHHmm(detailData.kyuukei_round_times) : '記録なし' }}</p>
        <p class="after">{{ detailData.kyuukei_times ? '(' +  timeToHHmm(detailData.kyuukei_times) + ')' : '' }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>所定労働時間</label>
      <div class="before-after">
        <p class="before">{{ detailData.syotei_work_times ? timeToHHmm(detailData.syotei_work_times) : 'なし' }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>法定内残業</label>
      <div class="before-after">
        <p class="before">{{ detailData.houteinai_zangyou_times ? timeToHHmm(detailData.houteinai_zangyou_times) : 'なし' }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>時間外</label>
      <div class="before-after">
        <p class="before">{{ detailData.jikangai_work_times ? timeToHHmm(detailData.jikangai_work_times) : 'なし' }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>深夜割増</label>
      <div class="before-after">
        <p class="before">{{ detailData.shinya_warimashi_times ? timeToHHmm(detailData.shinya_warimashi_times) : 'なし' }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>遅刻</label>
      <div class="before-after">
        <p class="before" v-bind:class="{ 'p-color-red': detailData.chikoku_times }">{{ detailData.chikoku_times ? timeToHHmm(detailData.chikoku_times) : 'なし' }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>早退</label>
      <div class="before-after">
        <p class="before" v-bind:class="{ 'p-color-red': detailData.soutai_times }">{{ detailData.soutai_times ? timeToHHmm(detailData.soutai_times) : 'なし' }}</p>
      </div>
    </div>

    <div class="form-item">
      <label>有給</label>
      <div class="before-after">
        <template v-if="Boolean(detailData.is_yuukyuu).valueOf()">
            <template v-if="detailData.yuukyuu_tani_type === OptionConst.SHINSEI_DIV.FULL_DAY">
              <p class="before">1日有給</p>
            </template>
            <template v-if="detailData.yuukyuu_tani_type === OptionConst.SHINSEI_DIV.AM">
              <p class="before">AM有給</p>
            </template>
            <template v-if="detailData.yuukyuu_tani_type === OptionConst.SHINSEI_DIV.PM">
              <p class="before">PM有給</p>
            </template>
        </template>
        <template v-else>
          <p class="before">なし</p>
        </template>
      </div>
    </div>

    <div class="buttons button-select">
      <button type="button" class="btn cancel" @click="close()"><i class="fa-solid fa-x"></i>閉じる</button>
    </div>
  </template>
  <template v-if="isResult">
    <div class="buttons">
      <button type="button" class="btn cancel" @click="close()"><i class="fa-solid fa-x"></i>閉じる</button>
    </div>
  </template>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue';
import { timeFormat, timeToHHmm, dateFormatMMDD } from "@/filters/common";
import OptionConst from '@/const/Option';
import { floor, isString } from "lodash";
import { DakokuDetailData } from '@/types/Dakoku';

export default defineComponent({
  props: {
    detailData: {
      type: Object as () => DakokuDetailData,
      default: null
    },
  },
  emits: {
    close: () => true,
  },
  setup(props, context) {

    const isResult = ref(false)

    const close = () => {
      context.emit('close');
    }

    const detailData = computed(() => {
      return props.detailData
    })

    const timeFormatter = (time: Date | string): string => {

      if (!time) {
        return '';
      }
      if (isString(time)) {
        time = new Date(time.replace(/-/g, "/"));
      }
      return time.toLocaleTimeString();

      // if (!time) {
      //   return '';
      // }

      // if (time === '00:00:00') {
      //   return '';
      // }

      // return timeFormat(time);
    }

    return {
      close,
      timeFormatter,
      timeToHHmm,
      dateFormatMMDD,
      detailData,
      isResult,
      OptionConst,
    }
  }
})
</script>

<style lang="scss" scoped>
@import '@scss/dialog/dakoku-syuusei.scss';
</style>
